import { CMSMedia, CMSMediaType, CMSRoomType } from '@/cms/fields/mappers/productDetailPageFieldsMapper'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import { space, tok, tokPx } from '@sunwebgroup/ui-components'
import { Icon, ListItem, Sheet, Typography } from '@sunwebgroup/ui-components/uds'
import { isEmpty } from 'ramda'
import * as React from 'react'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'
import { FullScreenGallery } from '../FullScreenGallery'
import { Gallery } from './Gallery'

const Wrapper = styled(ListItem)`
  display: flex;
  flex-direction: row;
  align-items: center;
  align-self: stretch;
  justify-content: space-between;
  padding: ${tokPx('primitives_space_16')};
  border: ${tokPx('semantics_dividers_tickness')} solid ${tok('semantics_dividers_color')};
  border-radius: ${tokPx('primitives_corner_radius_l')};
  width: 100%;

  &:last-of-type {
    margin-bottom: 0;
  }
`

const SheetContent = styled(Sheet.Content)`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_16')};
`

const RoomDescriptionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  gap: ${tokPx('primitives_space_8')};
  align-items: center;
  width: 100%;
`

const RoomNameAndCapacityWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  text-align: left;
`

const Characteristics = styled.ul`
  padding: 0;
  margin-left: ${space('primitives_space_20')};
  margin-top: 0;
`

const ChevronIcon = styled(Icon)`
  margin-left: auto;
`

const Heading = styled(Typography)`
  display: inline-block;
`

interface RoomTypeProps {
  roomType: CMSRoomType
}

export function RoomType({ roomType }: RoomTypeProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [showDetail, setShowDetail] = useState(false)
  const [clickedItemIndex, setClickedItemIndex] = useState(0)
  const { sendEvent } = useAnalytics()
  const intl = useIntl()

  const closeDetail = () => {
    setShowDetail(false)
  }

  const roomImagesMedia: CMSMedia[] = roomType.imageUrls?.map(url => ({
    src: url,
    alt: roomType.name,
    type: CMSMediaType.IMAGE,
  })) || []

  const openDetail = (index: number) => {
    setClickedItemIndex(index)
    setShowDetail(true)
  }
  const handleDetailImageClick = (index: number) => openDetail(index)

  const handleOnSettleDetail = () => {
    sendEvent(
      createPDPClickEvent({
        element: 'Slider',
        description: 'Roomtype image Gallery',
        result: 'Slide',
        type: roomType.name,
      }),
    )
  }

  const handleIsOpen = (isOpen: boolean) => {
    setIsOpen(isOpen)

    if (isOpen) {
      sendEvent(
        createPDPClickEvent({
          element: 'Element clicked',
          description: 'Roomtype description',
          result: 'Open modal with more details',
          type: roomType.name,
        }),
      )
    }
    else {
      sendEvent(
        createPDPClickEvent({
          element: 'Element clicked',
          description: 'Roomtype description',
          result: 'Close modal with more details',
          type: roomType.name,
        }),
      )
    }
  }

  return (
    <>
      {(roomType.characteristics || !isEmpty(roomType.imageUrls)) && (
        <>
          <Sheet.Root open={isOpen} onOpenChange={handleIsOpen}>
            <Sheet.Trigger>
              <Wrapper variant="outlined" as="button" type="button">
                <RoomDescriptionWrapper>
                  <Icon glyph="TwoPeople" size={27} />
                  <RoomNameAndCapacityWrapper>
                    <Typography $variant="body-l-bold-tight">{roomType.name}</Typography>
                    <Typography $variant="body-m-regular-tight">{roomType.capacityText}</Typography>
                  </RoomNameAndCapacityWrapper>
                  {(roomType.characteristics || !isEmpty(roomType.imageUrls)) && <ChevronIcon glyph="ChevronRight" size={16} />}
                </RoomDescriptionWrapper>
              </Wrapper>
            </Sheet.Trigger>
            <Sheet.Body side="right">
              <Sheet.Header titleLabel={intl.formatMessage({ id: 'roomtypes_room_description_heading' })} closeLabel={intl.formatMessage({ id: 'close' })} />
              <SheetContent>
                <Heading $variant="display-f-regular">{roomType.name}</Heading>

                {roomType.imageUrls?.length > 0 && (
                  <Gallery roomType={roomType} handleDetailImageClick={handleDetailImageClick} />
                )}

                {roomType.characteristics && (
                  <Characteristics>
                    {roomType.characteristics.map((characteristic, index) => (
                      <li key={index}>
                        <Typography $variant="body-m-regular-snug">{characteristic}</Typography>
                      </li>
                    ))}
                  </Characteristics>
                )}
              </SheetContent>
            </Sheet.Body>
          </Sheet.Root>
          {!isEmpty(roomType.imageUrls) && (
            <FullScreenGallery
              onClose={closeDetail}
              open={showDetail}
              startFromIndex={clickedItemIndex}
              onSettle={handleOnSettleDetail}
              media={roomImagesMedia}
            />
          )}
        </>
      )}
    </>
  )
}
